import { GPButtonItem, GPButtonType } from '@/types/button';
import {
  Box,
  Container,
  Divider,
  IconButton,
  List,
  MenuItem,
  Stack,
  SxProps,
  Tab,
  Tabs,
  Theme,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AddCircle,
  CloseCircle,
  Gpadduser,
  Gpdotmenu,
  Gpgiving2,
  Gpticket,
  Login,
  Personalcard,
} from 'givepulse-iconsax-react';
import { useRouter } from 'next/router';
import { ChangeEvent, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { makeStyles } from 'tss-react/mui';
import { GPButtonServer, GPMenuItem, GPMenuPopper, ListItemLink } from '..';
import { SubNavbar as SubNavbarType } from '../../types/sub-navbar';
import GPButton from '../GPButton';
import GPMenu from '../GPMenu';
import GPMobileDrawerBottom from '../GPMobileDrawerBottom';

const COLLAPSE_OFFSET = 180;

/**
 * The server-driven button schema doesn't send an `icon`, so map known
 * `label_raw` values to an iconsax glyph for the mobile overflow drawer.
 *
 * IMPORTANT: `label_raw` is only stable for buttons whose backend sets it
 * explicitly (`label_raw`/`forced_label_raw`) or from a string-backed enum.
 * For everything else the backend derives it as `Str::lower(<translated label>)`
 * (see GPMenuButton::setGPMenuButtons), so it varies by locale and by per-group
 * white-label settings — do NOT key icons off those. Only the enum-backed /
 * hardcoded values below are safe. A proper fix is a backend `icon` field.
 */
const getButtonIcon = (labelRaw?: string, color?: string) => {
  // Standard iconsax icons honor the `color` prop (fill/stroke), while the custom
  // `Gp*` icons ignore it and render `fill="currentColor"` — so also drive CSS color.
  const iconProps = { size: 20, color, style: color ? { color } : undefined };
  switch (labelRaw) {
    // Stable: explicit `label_raw` in the backend.
    case 'affiliate': // GroupTabResource
      return <Personalcard {...iconProps} />;
    case 'add_impact': // EventService
      return <AddCircle {...iconProps} />;
    case 'login': // EventController (event privacy)
      return <Login {...iconProps} />;
    case 'join': // EventController (event privacy)
      return <Gpadduser {...iconProps} />;
    // Stable: string-backed ButtonLabel enum (registration CTAs), lowercased.
    case 'register':
    case 'register again':
    case 'update registration':
    case 'confirm registration':
    case 'join waitlist':
      return <Gpticket {...iconProps} />;
    // Not stable — derived from a translated label, so English-only. Kept because
    // it currently renders for the common case; move to a backend `icon` field to
    // make it locale-safe.
    case 'donate':
      return <Gpgiving2 {...iconProps} />;
    // NOTE: `group` was removed and `checkout` is handled elsewhere — neither is
    // produced by the Laravel backend (legacy/Yii origin, unverified).
    default:
      return undefined;
  }
};

const visuallyHiddenSx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const useStyles = makeStyles()(theme => ({
  '@keyframes slide': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-77px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0%)',
    },
  },

  root: {
    position: 'fixed',
    left: 0,
    top: 64,
    width: '100%',
    animation: '$slide 0.2s',
    zIndex: theme.zIndex.appBar - 1,
    [theme.breakpoints.down('sm')]: {
      top: 60,
      // need this becuase of the add impact button at the bottom
      animation: 'none',
    },
  },

  rootNormal: {
    position: 'sticky',
    left: 0,
    top: 0,
    zIndex: theme.zIndex.appBar - 1,
  },

  toolbar: {
    backgroundColor: theme.palette.background.default,
  },

  tabIndicator: {
    display: 'none', // disable Mui indicator, using ::after instead
  },

  tabRoot: {
    height: '53px',
    minWidth: 'auto',
    paddingLeft: 0,
    paddingRight: 0,
    textTransform: 'none',
    position: 'relative',
    fontSize: 14,
    fontWeight: 500,
    '&:not(:last-child)': {
      marginRight: 40,
    },
    '&::after': {
      transition: theme.transitions.create('all'),
      opacity: 0,
      content: '""',
      position: 'absolute',
      bottom: 0,
      width: '100%',
      height: 4,
      backgroundColor: theme.palette.primary.main,
    },
    '&:hover': {
      '&::after': {
        opacity: 1,
      },
    },
    '&.Mui-selected::after': {
      opacity: 1,
    },
    '&.Mui-selected:hover': {
      '&::after': {
        backgroundColor: theme.palette.text.primary,
      },
    },
    [theme.breakpoints.down('md')]: {
      height: 53,
    },
  },

  actionButtonContainer: {
    display: 'flex',
    alignSelf: 'center',
    flexShrink: 0,
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },

  bottomBar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    animation: 'none',
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.text.secondary}`,
    padding: theme.spacing(2),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },

  manageContainer: {
    display: 'flex',
    position: 'fixed',
    alignItems: 'flex-start',
    flexDirection: 'row-reverse',
    top: 135,
    left: 0,
    overflow: 'hidden',
    zIndex: theme.zIndex.appBar - 1,
    height: '100%',
    pointerEvents: 'none',
  },

  manageMenu: {
    overflow: 'hidden',
    transition: theme.transitions.create('width'),
    backgroundColor: theme.palette.background.default,
    boxShadow: theme.shadows[0],
    marginBottom: 16,
    pointerEvents: 'auto',
  },

  manageItem: {
    width: 190,
  },

  manageButton: {
    transform: 'rotate(90deg) translate(0, -100%)',
    transformOrigin: 'top left',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    marginBottom: '60%',
    pointerEvents: 'auto',
  },
}));

type SubNavbarProps = {
  subNavbar: SubNavbarType;
  tabValue?: string;
  onTabChange?: (e: ChangeEvent<object>, tabValue: string) => void;
  useAsTabs?: boolean;
};

/**
 * @todo: being replaced by DashboardSubnav, should be easier with new theme.
 */
const SubNavbar = ({ subNavbar, onTabChange, tabValue, useAsTabs = false }: SubNavbarProps) => {
  const router = useRouter();
  const { pathname, push, query } = router;

  const currentPath = query.id ? pathname.replace('[id]', `${query.id}`) : '';

  const [sliceEnd, setSliceEnd] = useState(subNavbar.items?.length ?? 0);
  const [moreOpen, setMoreOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const tabs = useRef<HTMLDivElement>(null);
  const tabWidthStack = useRef<number[]>([]);
  const calculating = useRef(false);
  const sliceEndCopy = useRef(subNavbar.items?.length ?? 0);
  const anchorRef = useRef<HTMLDivElement | HTMLButtonElement | null>(null);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const prevHadCheckoutRef = useRef(false);
  const [checkoutAnnouncement, setCheckoutAnnouncement] = useState('');

  useEffect(() => {
    const checkoutButton = subNavbar.buttons?.find(b => b.label_raw === 'checkout');
    const hasCheckout = !!checkoutButton;

    if (hasCheckout && !prevHadCheckoutRef.current) {
      setCheckoutAnnouncement(checkoutButton.label ?? '');
    } else if (!hasCheckout) {
      setCheckoutAnnouncement('');
    }

    prevHadCheckoutRef.current = hasCheckout;
  }, [subNavbar.buttons]);

  const moreItemsTabValue = 'more';

  // Collapse items to "More" dropdown if it's overflowed
  const collapseItems = () => {
    if (!subNavbar.items) return;
    if (subNavbar.items.length < 3) return;
    if (calculating.current) return;

    calculating.current = true;

    if (tabs.current) {
      // Tab container div width
      const windowWidth = tabs.current.offsetWidth + COLLAPSE_OFFSET;
      const buttons = (tabs.current.firstChild?.firstChild?.childNodes ??
        []) as NodeListOf<HTMLElement>;

      console.log({
        windowWidth,
        tabsOffsetWidth: tabs.current.offsetWidth,
        tabsScrollWidth: tabs.current.scrollWidth,
        sliceEnd: sliceEndCopy.current,
      });

      console.log(tabs.current);

      // Actual children total width
      let contentWidth = 0;
      buttons.forEach(btn => {
        contentWidth += btn.getBoundingClientRect().width + 40;
      });

      if (windowWidth && contentWidth && buttons) {
        // The last item we want to keep before collapse
        let idx = sliceEndCopy.current - 1;

        if (contentWidth > windowWidth) {
          // Pushing item into collapsed if it's overflow
          while (contentWidth > windowWidth && idx > 0) {
            const buttonWidth = buttons[idx].getBoundingClientRect().width;
            contentWidth -= buttonWidth;
            contentWidth -= 40;

            tabWidthStack.current.push(buttonWidth + 40);

            idx -= 1;
          }

          // Minimum 2 items collapsed. If only one item collapsed, collapse one more
          if (idx === subNavbar.items.length - 2) {
            const buttonWidth = buttons[idx].getBoundingClientRect().width;

            tabWidthStack.current.push(buttonWidth + 40);
            idx -= 1;
          }

          setSliceEnd(idx + 1);
          sliceEndCopy.current = idx + 1;
        } else if (contentWidth < windowWidth) {
          while (
            tabWidthStack.current.length > 0 &&
            contentWidth + tabWidthStack.current[tabWidthStack.current.length - 1] < windowWidth
          ) {
            const lastWidth = tabWidthStack.current.pop() ?? 0;
            contentWidth += lastWidth;
            idx += 1;
          }

          // If only one left in collapsed, pop it
          if (idx === subNavbar.items.length - 2) {
            tabWidthStack.current.pop();
            idx += 1;
          }

          setSliceEnd(idx + 1);
          sliceEndCopy.current = idx + 1;
        }
      }
    }
    calculating.current = false;
  };

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      collapseItems();
    });

    window.addEventListener('resize', collapseItems);

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', collapseItems);
    };
  }, []);

  const pageEntityType = useMemo(() => {
    const entitiesWithSubnavbar = ['badge', 'event', 'group', 'user'] as const;
    for (const entity of entitiesWithSubnavbar) {
      if (router.pathname.startsWith(`/${entity}`)) {
        const [firstLetter, ...rest] = entity;
        return `${firstLetter.toUpperCase()}${rest.join('')}`;
      }
    }
  }, [router.pathname]);

  const { classes } = useStyles();
  const { t } = useTranslation();

  const handleTabChange = (e: ChangeEvent<object>, value: string) => {
    // opens a menu instead of routing
    if (value === moreItemsTabValue) return;

    if (onTabChange) {
      onTabChange(e, value);
      return;
    }

    push(value);
  };

  const handleMoreClose = (event: Event | SyntheticEvent<Element, Event>) => {
    if (anchorRef?.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setMoreOpen(false);
  };

  const handleMoreKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setMoreOpen(false);
      anchorRef.current?.focus();
    }
  };

  const { ref: myRef, inView: myElementIsVisible } = useInView({
    threshold: 0,
    rootMargin: '-77px 0px 1000% 0px',
    skip: typeof window === 'undefined',
    initialInView: true,
  });

  const calculatedTabValue = useMemo(() => {
    const currentTabValue = tabValue ?? currentPath;
    if (!subNavbar.items) return null;
    const currentTabIsRendered = subNavbar.items
      .slice(0, sliceEnd)
      .some(item => item.path === currentTabValue);
    return currentTabIsRendered ? currentTabValue : moreItemsTabValue;
  }, [subNavbar.items, sliceEnd, tabValue, currentPath]);

  return (
    <Box ref={myRef}>
      <Box
        sx={{
          borderBottom: isDarkMode ? '1px solid #878787' : 'none',
          boxShadow: isDarkMode ? 'none' : theme.shadows[0],
        }}
        className={`${myElementIsVisible ? classes.rootNormal : classes.root}`}
      >
        <Toolbar
          className={classes.toolbar}
          disableGutters
          sx={{
            minHeight: [53, 53],
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                }}
              >
                {subNavbar.items && (
                  <Tabs
                    ref={tabs}
                    classes={{
                      indicator: classes.tabIndicator,
                    }}
                    value={calculatedTabValue}
                    onChange={handleTabChange}
                    sx={{
                      opacity: 1,
                      '&.Mui-selected': {
                        opacity: 1,
                      },
                    }}
                  >
                    {subNavbar.items.slice(0, sliceEnd).map(
                      item =>
                        !item.is_divider &&
                        ((useAsTabs && (
                          <Tab
                            key={`${item.label_raw}-${item.unique_id}`}
                            sx={{
                              height: 53,
                              minWidth: 'auto',
                              px: 0,
                              textTransform: 'none',
                              position: 'relative',
                              fontSize: 14,
                              fontWeight: 500,
                              '&:not(:last-child)': {
                                mr: '40px',
                              },
                            }}
                            label={item.label}
                            value={item.path}
                            disabled={item.disabled}
                          />
                        )) || (
                            <Tab
                              tabIndex={0}
                              key={`${item.label_raw}-${item.unique_id}`}
                              // sx={{
                              //   height: 53,
                              //   minWidth: 'auto',
                              //   px: 0,
                              //   textTransform: 'none',
                              //   position: 'relative',
                              //   fontSize: 14,
                              //   fontWeight: 500,
                              //   '&:not(:last-child)': {
                              //     mr: '40px',
                              //   },
                              // }}
                              label={item.label}
                              value={item.path}
                              disabled={item.disabled}
                              disableRipple
                              sx={{
                                '&:focus-visible': {
                                  fontWeight: 'bold',
                                  color: 'theme.palette.text.primary',
                                  opacity: 1,
                                },
                              }}
                            />
                          )),
                    )}
                    {sliceEnd < subNavbar.items.length && (
                      <Tab
                        tabIndex={0}
                        ref={anchorRef as React.RefObject<HTMLDivElement>}
                        // sx={{
                        //   height: 53,
                        //   minWidth: 'auto',
                        //   px: 0,
                        //   textTransform: 'none',
                        //   position: 'relative',
                        //   fontSize: 14,
                        //   fontWeight: 500,
                        //   '&:not(:last-child)': {
                        //     mr: '40px',
                        //   },
                        // }}
                        label={t('More')}
                        value="more"
                        onClick={() => {
                          setMoreOpen(true);
                        }}
                        aria-haspopup={true}
                        aria-expanded={moreOpen}
                        disableRipple
                        sx={{
                          '&:focus-visible': {
                            fontWeight: 'bold',
                            color: 'theme.palette.text.primary',
                            opacity: 1,
                          },
                        }}
                      />
                    )}
                  </Tabs>
                )}
                {subNavbar.items && sliceEnd < subNavbar.items.length && (
                  <GPMenuPopper
                    id="sub-navbar-menu-more"
                    anchorRef={anchorRef as React.RefObject<HTMLButtonElement>}
                    open={moreOpen}
                    handleClose={handleMoreClose}
                    menuProps={{
                      onKeyDown: handleMoreKeyDown,
                    }}
                  >
                    {subNavbar.items.slice(sliceEnd).map(item => {
                      const isSelected = currentPath.startsWith(item?.path ?? '');
                      return (
                        item.path && (
                          <MenuItem
                            key={item.unique_id}
                            aria-selected={isSelected}
                            disableGutters
                            tabIndex={0}
                            sx={{
                              px: theme => theme.spacing(3),
                              py: theme => theme.spacing(1.25),
                              backgroundColor: theme =>
                                isSelected ? theme.palette.action.hover : undefined,
                            }}
                            onClick={() => {
                              if (item.path) router.push(item.path);
                            }}
                            autoFocus={isSelected}
                          >
                            {item.label}
                          </MenuItem>
                        )
                      );
                    })}
                  </GPMenuPopper>
                )}
              </Box>
              <Box role="status" aria-live="polite" aria-atomic="true" sx={visuallyHiddenSx}>
                {checkoutAnnouncement &&
                  t('{{label}}. Ready to complete your donation.', {
                    label: checkoutAnnouncement,
                  })}
              </Box>
              {!!subNavbar?.buttons?.length && (
                <SecondaryNavActions buttons={subNavbar.buttons} labelOverrides={[]} />
              )}
            </Box>
          </Container>
        </Toolbar>
      </Box>
      {!!subNavbar.manage_buttons?.length && (
        <Box
          id="GPAnchorManage"
          className={classes.manageContainer}
        // role="menubar"
        >
          <>
            <GPButton
              color="primary"
              variant="contained"
              className={classes.manageButton}
              onClick={() => {
                setManageOpen(s => !s);
              }}
              aria-expanded={manageOpen ? 'true' : undefined}
              aria-haspopup="true"
              aria-controls={manageOpen ? 'manage-dropdown' : undefined}
              id="manage-dropdown-button"
              ariaLabel={t('Manage this {{entity}} menu', {
                entity: pageEntityType ?? 'page', // doesn't account for white-labeling
              })}
            >
              {t('Manage')}
            </GPButton>
            {manageOpen ? (
              <Box
                className={classes.manageMenu}
                style={{
                  width: manageOpen ? 190 : 0,
                  height: manageOpen ? 'auto' : 0,
                }}
                // role="menu"
                aria-labelledby="manage-dropdown-button"
                id="manage-dropdown"
              >
                {subNavbar.manage_buttons.map(item => (
                  <GPMenuItem item={item} key={item.unique_id} className={classes.manageItem} />
                ))}
              </Box>
            ) : null}
          </>
        </Box>
      )}
    </Box>
  );
};

export default SubNavbar;

const responseStyleSX: SxProps<Theme> = theme => ({
  [theme.breakpoints.down('sm')]: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    padding: 2,
    backgroundColor: 'background.default',
    zIndex: theme.zIndex.appBar,
    width: 1,
    boxShadow: 4,
    justifyContent: 'center',
  },
});

const SecondaryNavActions = ({
  buttons,
  labelOverrides = [],
}: {
  buttons: GPButtonType[];
  labelOverrides?: string[];
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const smDown = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
  const [overflowOpen, setOverflowOpen] = useState(false);

  const overflowButtons = buttons.slice(2);

  const checkoutButton = buttons.find(b => b.label_raw === 'checkout');

  const firstTwoActions = buttons.slice(0, 2).map((button, isSecondary) => (
    <GPButtonServer
      key={button.unique_id}
      button={{ ...button, label: labelOverrides[isSecondary] ?? button.label }}
      buttonProps={{
        sx: {
          minWidth: isSecondary ? undefined : '16ch',
          ...(button.label_raw === 'checkout' && {
            width: { xs: '100%', sm: 'auto' },
          }),
        },
        variant: isSecondary ? 'outlined' : 'contained',
        color: 'primary',
        ariaLabel: labelOverrides[isSecondary] ?? button.label,
      }}
    />
  ));

  const dropdownButtons = overflowButtons.filter(b => b.items && b.items.length > 0);
  const simpleButtons = overflowButtons.filter(b => b.path && b.path.length > 0);

  return (
    <Stack
      direction="row"
      role="toolbar"
      aria-label={t('Event actions')}
      sx={[
        {
          gap: 1,
          alignSelf: 'center',
        },
        ...(Array.isArray(responseStyleSX) ? responseStyleSX : [responseStyleSX]),
      ]}
    >
      {checkoutButton && (
        <Typography component="h2" sx={visuallyHiddenSx}>
          {checkoutButton.label}
        </Typography>
      )}
      {firstTwoActions}
      {overflowButtons.length > 0 &&
        (smDown ? (
          <>
            <GPButton
              round
              variant="outlined"
              color="primary"
              ariaLabel={t('more actions')}
              aria-haspopup="true"
              aria-expanded={overflowOpen ? 'true' : undefined}
              aria-controls={overflowOpen ? 'sub-navbar-collapse-drawer' : undefined}
              onClick={() => setOverflowOpen(true)}
            >
              <Gpdotmenu
                size={10}
                style={{ rotate: '90deg', margin: '0px', width: 'inherit', height: 'inherit' }}
              />
            </GPButton>
            <GPMobileDrawerBottom
              open={overflowOpen}
              handleOpen={() => setOverflowOpen(true)}
              handleClose={() => setOverflowOpen(false)}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  px: 2,
                  pt: 1,
                }}
              >
                <IconButton
                  aria-label={t('Close')}
                  size="small"
                  onClick={() => setOverflowOpen(false)}
                >
                  <CloseCircle size={24} />
                </IconButton>
              </Box>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  width: 1,
                }}
              >
                {simpleButtons.length > 0 && (
                  <Box sx={theme => ({
                    '& > *:not(:last-child)': {
                      marginBottom: theme.spacing(1),
                    },
                  })}>
                    {simpleButtons.map(button => (
                      <GPButtonServer
                        key={`${button.label_raw}-${button.unique_id}`}
                        button={button}
                        buttonProps={{
                          round: true,
                          fullWidth: true,
                          disabled: button.disabled,
                          startIcon: getButtonIcon(button.label_raw, primaryColor),
                          sx: { justifyContent: 'flex-start' },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
              <Box
                sx={theme => ({
                  width: 1,
                  '& > *:not(:last-child)': {
                    marginBottom: theme.spacing(2),
                  },
                })}
              >
                {dropdownButtons.length > 0 && (
                  <Box>
                    {dropdownButtons.map(button => (
                      <Box key={button.unique_id}>
                        <Box
                          sx={{
                            py: 2,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: 'center', justifyContent: 'flex-start', pl: 2 }}
                          >
                            {getButtonIcon(button.label_raw, primaryColor)}
                            <Typography variant="h6" align="left">
                              {button.label}
                            </Typography>
                          </Stack>
                        </Box>
                        <Divider />
                        <Box
                          sx={{
                            px: 5,
                          }}
                        >
                          <List disablePadding>
                            {button.items?.map(
                              item =>
                                !item.is_divider && (
                                  <>
                                    <Box
                                      sx={{
                                        py: 1,
                                      }}
                                    >
                                      <ListItemLink disablePadding external href={item.path}>
                                        {item.label}
                                      </ListItemLink>
                                    </Box>
                                    <Divider />
                                  </>
                                ),
                            )}
                          </List>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </GPMobileDrawerBottom>
          </>
        ) : (
          <GPMenu
            id="sub-navbar-collapse-button"
            items={overflowButtons as GPButtonItem[]}
            buttonProps={{
              round: true,
              variant: 'outlined',
              color: 'primary',
              children: (
                <Gpdotmenu
                  size={10}
                  style={{ rotate: '90deg', margin: '0px', width: 'inherit', height: 'inherit' }}
                />
              ),
              ariaLabel: t('more actions'),
            }}
          />
        ))}
    </Stack>
  );
};
